/**
 * Нормализация ответа GET /notifications/summary (Bell Addendum v1.1, раздел 8).
 *
 * Контракт бэкенда зафиксирован (frozen), поэтому читаем ровно его поля:
 * item.unread — признак «новое», item.status — это lifecycle события
 * (active / resolved), и путать их нельзя. Отдельного body бэкенд не отдаёт,
 * подзаголовок собирается из hierarchy_path.
 *
 * Недостающие уровни иерархии просто пропускаются, а не рисуются как «—».
 * Выдумывать данные (счётчик из другого источника, фиктивные заголовки)
 * нельзя: колокольчик обязан показывать ровно то, что сказал backend.
 */

const HIERARCHY_KEYS = [
  'factory_name',
  'workshop_name',
  'section_name',
  'room_name',
  'camera_name',
];

/** Готовый hierarchy_path бэкенда главнее — он уже включает камеру. */
export const buildEntityPath = (item = {}) => {
  const provided = typeof item.hierarchy_path === 'string' ? item.hierarchy_path.trim() : '';
  if (provided) return provided;
  return HIERARCHY_KEYS.map((key) => item[key]).filter(Boolean).join(' / ');
};

const normalizeItem = (item = {}) => {
  const eventId = item.event_id ?? item.id ?? null;
  return {
    eventId,
    ruleId: item.rule_id ?? null,
    title: item.title || 'Новое нарушение',
    url: item.url || (eventId ? `/violations/${eventId}` : '/violations'),
    // Строго item.unread: status здесь означает active / resolved.
    unread: item.unread === true,
    confirmedAt: item.confirmed_at || '',
    cameraId: item.camera_id ?? null,
    // ТЗ №2 §10.3 Notifications: timestamp в зоне камеры, если backend её отдал.
    displayTimezone: item.display_timezone || '',
    cameraName: item.camera_name || '',
    path: buildEntityPath(item),
    lifecycleStatus: item.status || '',
  };
};

export const normalizeNotificationSummary = (data) => {
  const items = Array.isArray(data?.items) ? data.items.map(normalizeItem) : [];
  const unreadCount = Number.isFinite(data?.unread_count)
    ? data.unread_count
    : items.filter((item) => item.unread).length;

  return { unreadCount, items };
};

/** При нуле badge скрывается — ТЗ прямо предпочитает скрытый вариант. */
export const formatUnreadBadge = (count) => {
  if (!Number.isFinite(count) || count <= 0) return '';
  return count > 9 ? '9+' : String(count);
};

/**
 * 404/501 значит «эндпоинта ещё нет» — колокольчик просто не рисуется.
 * Всё остальное (5xx, сеть) — обычная ошибка: показываем error-state с
 * «Повторить», потому что эндпоинт есть и может ответить в следующий раз.
 */
export const isMissingEndpoint = (error) => {
  const status = error?.response?.status;
  return status === 404 || status === 501;
};
